import { Drawer } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import ProductQuickView from 'components/Shared/ProductQuickView';
import { SvgIcon } from 'components/Shared/SvgIcon';
import { WithStyles } from 'decorators/withStyles';
import { IPermissions } from 'interfaces/IPermissions';
import { IProductNew } from 'interfaces/product';
import { ITheme } from 'interfaces/theme';
import { enTypeUser } from 'interfaces/user';
import React, { PureComponent } from 'react';
import rxjsOperators from 'rxjs-operators';
import { analyticsG4, enFeatures } from 'services/AnalyticsG4';
import authService from 'services/auth';
import { productNewService } from 'services/productNewService';
import { themeService } from 'services/themeService';

import Campaigns from './Campaigns';
import Preview from './Preview';

interface IState {
  useTheme: boolean;
  selectedTheme?: ITheme;
  selectedProduct: IProductNew;
  isChangingTheme: boolean;
  typeUser: number;
  permissions: IPermissions;
}

interface IProps {
  creatingTheme?: () => void;
  classes?: any;
}

@WithStyles(theme => ({
  drawer: {
    background: '#FFF !important'
  },
  drawerOptions: {
    overflow: 'scroll',
    height: 'calc(100vh - 35px)',
    position: 'relative',
    '&::-webkit-scrollbar': {
      width: '6px',
      backgroundColor: '#f1d5d5'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#555',
      borderRadius: 10
    }
  },
  drawerOptionsHeader: {
    padding: '20px 20px 0 20px',
    backgroundColor: '#F3F3F3',
    width: 448
  },
  drawerOptionsBody: {
    padding: '0px 20px 0 20px',
    backgroundColor: '#FFF'
  },
  titleClose: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  themeTitle: {
    fontWeight: 'bold',
    color: theme.variables.colors['primaryColor'],
    margin: 0
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '0px solid #000',
    borderRadius: '100%',
    width: 40,
    height: 40,
    color: '#596375',
    fontWeight: 'bold',
    backgroundColor: '#D8D8D8',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    transition: 'background 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: '#596375',
      '& svg': {
        fill: '#fff'
      }
    },
    '& svg': {
      width: '30%',
      fontWeight: 'bold',
      transition: 'fill 0.3s ease-in-out',
      fill: '#596375'
    }
  },
  themeType: {
    textTransform: 'capitalize',
    color: '#596375',
    marginBottom: 20
  },
  labelSwitch: {
    fontWeight: 'bold',
    color: '#596375'
  },
  withoutThemeContent: {
    height: 300,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      width: 100
    }
  },
  withoutThemeMessage: {
    color: '#596375',
    width: 280,
    textAlign: 'center'
  },
  withoutThemeSpan: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: '#009358'
  },
  withoutThemeCloseButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '20px 20px 0 0'
  }
}))
export default class ActionsTheme extends PureComponent<IProps, IState> {
  constructor(props: any) {
    super(props);
    this.state = {
      isChangingTheme: false,
      useTheme: false,
      selectedProduct: null,
      typeUser: undefined,
      permissions: undefined
    };
  }

  componentDidMount() {
    themeService
      .getSelectedTheme()
      .pipe(
        rxjsOperators.combineLatest(productNewService.getSelectedProduct()),
        rxjsOperators.tap(() => this.setState({ useTheme: false })),
        rxjsOperators.filter(([selectedTheme, selectedProduct]) => !!selectedTheme && !!selectedProduct),
        rxjsOperators.bindComponent(this)
      )
      .subscribe(([selectedTheme, selectedProduct]) => {
        this.setState({
          useTheme: selectedTheme.linkedProducts.includes(selectedProduct.id)
        });
        this.setState({ isChangingTheme: true });
        setTimeout(() => this.setState({ isChangingTheme: false, selectedTheme }), 200);
        this.setState({ selectedTheme });
      });

    productNewService
      .getSelectedProduct()
      .pipe(rxjsOperators.bindComponent(this))
      .subscribe(selectedProduct => this.setState({ selectedProduct }));

    authService
      .getTypeUser()
      .pipe(rxjsOperators.combineLatest(authService.getPermissions()), rxjsOperators.bindComponent(this))
      .subscribe(([typeUser, permissions]) => this.setState({ typeUser, permissions }));
  }

  isLinkedTheme = (): void => {
    const { selectedProduct, useTheme } = this.state;

    if (useTheme) {
      this.setState({ useTheme: false });
      themeService
        .setLinkedTheme()
        .pipe(rxjsOperators.take(1), rxjsOperators.loader(), rxjsOperators.bindComponent(this))
        .subscribe(resp => {
          analyticsG4.sendSuccess(enFeatures.TEMA);
          themeService.setDataTheme(resp.theme);
          productNewService.setSelectedProduct({ ...selectedProduct, theme: false });
        });
    } else {
      this.setState({ useTheme: true });
      themeService
        .setLinkedTheme()
        .pipe(rxjsOperators.take(1), rxjsOperators.loader(), rxjsOperators.bindComponent(this))
        .subscribe(resp => {
          analyticsG4.sendSuccess(enFeatures.TEMA);
          themeService.setDataTheme(resp.theme);
          productNewService.setSelectedProduct({ ...selectedProduct, theme: true });
        });
    }

    productNewService.refreshProducts();
  };

  onClose = () => {
    themeService.setSelectedTheme(null);
    themeService.setIsCreating(false);
    this.setState({ selectedTheme: null });
  };

  render() {
    const { classes } = this.props;
    const { isChangingTheme, useTheme, selectedTheme, selectedProduct, typeUser, permissions } = this.state;

    if (!selectedProduct || !selectedTheme) {
      return null;
    }

    return (
      <Drawer
        anchor="right"
        open={!!selectedTheme}
        className={classes.drawerOptions}
        classes={{ paper: classes.drawer }}
        ModalProps={{
          classes: { root: classes.removeBackdrop },
          disableBackdropClick: true,
          onBackdropClick: this.onClose
        }}
        transitionDuration={{ enter: 500, exit: 500 }}
      >
        <div className={classes.drawerOptionsHeader}>
          <div className={classes.titleClose}>
            <Fade timeout={{ enter: 300, exit: 300 }} in={!isChangingTheme}>
              <h2 className={classes.themeTitle}>{selectedTheme.name}</h2>
            </Fade>
            <span className={classes.closeButton} onClick={this.onClose}>
              <SvgIcon name="closeIcon" />
            </span>
          </div>
          <Fade in={!isChangingTheme}>
            <span className={classes.themeType}>{`Template ${selectedTheme.type}`}</span>
          </Fade>
          <ProductQuickView />
        </div>
        <div className={classes.drawerOptionsBody}>
          {(typeUser == enTypeUser.producer || permissions.myProducts.canVinculate) && (
            <FormControlLabel
              control={
                <Switch disableRipple checked={useTheme} onChange={this.isLinkedTheme} value={`${selectedTheme}`} />
              }
              classes={{ label: classes.labelSwitch }}
              label="Usar este template"
            />
          )}
          <Preview onClose={this.onClose} isChangingTheme={isChangingTheme} selectedTheme={selectedTheme} />
          <Campaigns />
        </div>
      </Drawer>
    );
  }
}
